import { z } from 'zod';
import { AssetTypeSchema, AssetStatusSchema } from './asset-api.dto';

/**
 * Cart completeness status for surgical cases
 */
export const CartCompletenessStatusSchema = z.union([
  z.literal('Arrived'),
  z.literal('Dispatched'),
  z.literal('Dispatched - Incomplete'),
  z.literal('Ready for Dispatch'),
  z.literal('Trays Unavailable'),
  z.literal('In Progress'),
  z.literal('Incomplete'),
  z.literal('Not Started'),
]);
export type CartCompletenessStatus = z.infer<typeof CartCompletenessStatusSchema>;

/**
 * Pagination parameters for fetching cases
 */
export interface CasePaginationParams {
  /** Page number (0-based) */
  page: number;
  /** Number of items per page */
  pageSize: number;
  /** Start date for filtering cases (inclusive) */
  startDate?: string;
  /** End date for filtering cases (inclusive) */
  endDate?: string;
  /** Restrict to cases at a specific location */
  locationId?: string;
  /** Whether to include full checklist items */
  includeChecklistItems?: boolean;
}

/**
 * Individual case item for workflow summary cards
 */
export interface WorkflowCaseItemDTO {
  /** Case ID (UUID) */
  id: string;
  /** Human readable Case ID (e.g., "1029999") */
  caseHospitalId?: string;
  /** Surgery start time formatted (e.g., "8:30am") */
  surgeryTime: string;
  /** Time until surgery (e.g., "2hrs", "59mins") */
  dueTime: string;
  /** Action button text (e.g., "Dispatch", "Build", "View") */
  action?: 'Dispatch' | 'Build' | 'View';
  /** Case type for actionable cases */
  type?: 'Ready for Dispatch' | 'Build Incomplete' | 'Build Not Started';
  /** Number of trays for blocked cases */
  trays?: number;
  /** Whether this case is priority (red styling) */
  isPriority?: boolean;
}

/**
 * User information for in-progress cases
 */
export interface WorkflowUserDTO {
  /** User initials (e.g., "HH") */
  initials: string;
  /** Full name (e.g., "H. Hogan") */
  name: string;
}

/**
 * Workflow summary data for a specific date
 */
export interface WorkflowSummaryDTO {
  /** Selected date */
  date: string;
  /** Total cases due for this date */
  totalCasesDue: number;
  /** Completion percentage for this date */
  completionPercentage: number;
  /** Actionable cases (cases to be built or dispatched) */
  actionableCases: WorkflowCaseItemDTO[];
  /** Blocked cases (cases with trays unavailable) */
  blockedCases: WorkflowCaseItemDTO[];
  /** In progress cases */
  inProgressCases: {
    total: number;
    user?: WorkflowUserDTO;
  };
  /** Dispatched cases */
  dispatchedCases: {
    total: number;
    complete: number;
    incomplete: number;
  };
}

/**
 * Response from the workflow summary API
 */
export interface WorkflowSummaryResponseDTO {
  /** Summary data for the requested date */
  summary: WorkflowSummaryDTO;
  /** Available date options with completion percentages */
  dateOptions: Array<{
    date: string;
    label: string;
    completionPercentage: number;
  }>;
}

/**
 * Case status enum (from Prisma)
 */
export enum CaseStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
}

export const CaseStatusSchema = z.nativeEnum(CaseStatus);

/**
 * Case build status enum (from Prisma)
 */
export enum CaseBuildStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  INCOMPLETE = 'INCOMPLETE',
  COMPLETE = 'COMPLETE',
  BLOCKED = 'BLOCKED',
}

export const CaseBuildStatusSchema = z.nativeEnum(CaseBuildStatus);

/**
 * Closing method enum (from Prisma) - how a case was closed when marked FINISHED
 */
export enum ClosingMethod {
  CLOSED_BY_RELEASE = 'CLOSED_BY_RELEASE',
  CLOSED_BY_SYSTEM = 'CLOSED_BY_SYSTEM',
}

export const ClosingMethodSchema = z.nativeEnum(ClosingMethod);

/**
 * Editing session metadata for a case build cart
 */
export const CaseEditingSessionDtoSchema = z
  .object({
    /** Case identifier the session belongs to */
    caseId: z.string().optional(),
    /** User identifier who owns the editing session */
    userId: z.string().optional(),
    /** Optional user initials for display */
    userInitials: z.string().optional(),
    /** When the editing session started */
    startedAt: z.string(),
    /** Last activity timestamp for the session */
    lastActivity: z.string(),
    /** Expiration timestamp (server enforced timeout) */
    expiresAt: z.string(),
  });
export type CaseEditingSessionDto = z.infer<typeof CaseEditingSessionDtoSchema>;

/**
 * Session status response DTO
 * Used for status endpoint response
 */
export const SessionStatusResponseDtoSchema = z
  .object({
    /** Whether the session is currently active */
    isActive: z.boolean(),
    /** Case identifier the session belongs to */
    caseId: z.string().optional(),
    /** User identifier who owns the editing session */
    userId: z.string().optional(),
    /** Optional user initials for display */
    userInitials: z.string().optional(),
    /** Time remaining in seconds */
    timeRemaining: z.number().optional(),
    /** Whether the session is in grace period */
    isInGracePeriod: z.boolean().optional(),
    /** Information about who has the session locked */
    lockedBy: z
      .object({
        userId: z.string().optional(),
        userInitials: z.string().optional(),
      })
      .optional(),
  });
export type SessionStatusResponseDto = z.infer<
  typeof SessionStatusResponseDtoSchema
>;

/**
 * DTO for updating case build status
 */
export interface UpdateBuildStatusDto {
  /** New build status for the case checklist */
  buildStatus: CaseBuildStatus;
}

/**
 * DTO for updating dispatch status
 */
export interface UpdateDispatchStatusDto {
  /** Dispatch status flag */
  dispatchStatus: boolean;
}

/**
 * DTO for confirming all required assets are present in the case room
 */
export interface VerifyAssetsDto {
  /** Full name of the confirming user, stored as the case's verifiedByName */
  verifiedBy: string;
}

/**
 * DTO for updating tracked items (trays, instruments, equipment, etc.)
 */
export interface UpdateTrackedItemsDto {
  assetId: string;
}

/**
 * DTO for assigning multiple tracked items in batch
 */
export interface AssignTrackedItemsBatchDto {
  /** Asset IDs of tracked items to assign */
  assetIds: string[];
  /** Optional asset ID of the first discovered case cart */
  firstCartAssetId?: string;
}

export interface ReserveTrackedItemsBatchDto {
  /** Asset IDs of tracked items to reserve */
  trayAssetIds: string[];
  /** Optional asset ID of the first discovered case cart */
  firstCartAssetId?: string;
}

/**
 * DTO for updating a single untracked item (supply or single-packed instrument)
 */
export interface UpdateSuppliesAndInstrumentsItemDto {
  /** Catalog item identifier */
  catalogItemId: string;
  /** Whether allocated */
  allocated: boolean;
  /** Assigned asset id (may be null for untracked items) */
  assetId?: string;
  /** Optional notes */
  notes?: string;
}

/**
 * DTO for updating supplies and single-packed instruments
 */
export interface UpdateSuppliesAndInstrumentsDto {
  /** Supplies to update */
  supplies: UpdateSuppliesAndInstrumentsItemDto[];
  /** Single-packed instruments to update */
  singlePackedInstruments: UpdateSuppliesAndInstrumentsItemDto[];
  /** Untracked instruments to update */
  instruments?: UpdateSuppliesAndInstrumentsItemDto[];
}

/**
 * DTO for updating an unexpected item
 */
export interface UpdateUnexpectedItemDto {
  /** Unexpected item id */
  itemId: string;
}

/**
 * DTO for updating sidehub available status
 */
export interface UpdateSidehubAvailableStatusDto {
  /** Available status flag */
  available: boolean;
}

/**
 * Checklist tracked item DTO
 */
export const ChecklistTrackedItemDtoSchema = z
  .object({
    /** Unique identifier for this checklist item row (not the physical asset ID) */
    checklistItemId: z.string(),
    /** Tracked catalog item id */
    catalogItemId: z.string(),
    /** Whether allocated */
    allocated: z.boolean(),
    /** Assigned asset id */
    assetId: z.string().nullish(),
    /** Released asset id retained for history when assetId is cleared on release */
    releasedAssetId: z.string().nullish(),
    /** Tray assembly checklist ID captured at allocation-time */
    trayAssemblyChecklistId: z.string().nullish(),
    /** Notes */
    notes: z.string().nullish(),
    /** Whether this item is unexpected (exceeds preference card quantity) */
    isUnexpected: z.boolean().optional(),
    /** Whether this unexpected item has been accepted by the user */
    isUnexpectedAccepted: z.boolean().optional(),
    /** Whether this tracked item was released from case allocation */
    released: z.boolean(),
    /** Timestamp when this tracked item was released from case allocation */
    releasedTimestamp: z.string().nullish(),
    /** Whether this tracked item was reassigned to another case */
    reassigned: z.boolean(),
    /** Timestamp when this tracked item was reassigned */
    reassignedTimestamp: z.string().nullish(),
    /** Internal case UUID this tracked item was reassigned to */
    reassignedCaseId: z.string().nullish(),
  });
export type ChecklistTrackedItemDto = z.infer<
  typeof ChecklistTrackedItemDtoSchema
>;

/**
 * Checklist untracked item DTO
 */
export const ChecklistUntrackedItemDtoSchema = z
  .object({
    /** Unique identifier for this checklist item row (not the physical asset ID) */
    checklistItemId: z.string(),
    /** Untracked catalog item id */
    catalogItemId: z.string(),
    /** Whether allocated */
    allocated: z.boolean(),
    /** Assigned asset id */
    assetId: z.string().nullish(),
    /** Notes */
    notes: z.string().nullish(),
  });
export type ChecklistUntrackedItemDto = z.infer<
  typeof ChecklistUntrackedItemDtoSchema
>;

/**
 * Catalog item group DTO for tracked items
 */
export const CatalogItemGroupDtoSchema = z
  .object({
    /** Catalog item identifier */
    catalogItemId: z.string(),
    /** Display name for the catalog item */
    name: z.string(),
    /** Product or reference number for the catalog item (Instrument Type ID) */
    productNumber: z.string(),
    /** Manufacturer name associated with the catalog item */
    manufacturer: z.string(),
    /** Catalog item description if available */
    description: z.string().optional(),
    /** Additional metadata for the catalog item */
    metadata: z.record(z.unknown()).nullish(),
    /** Note from preference card for this catalog item */
    note: z.string().optional(),
    /** Checklist items for this catalog item */
    items: z.array(ChecklistTrackedItemDtoSchema),
  });
export type CatalogItemGroupDto = z.infer<typeof CatalogItemGroupDtoSchema>;

/**
 * Untracked catalog item group DTO
 */
export const UntrackedCatalogItemGroupDtoSchema = z
  .object({
    /** Catalog item identifier */
    catalogItemId: z.string(),
    /** Display name for the catalog item */
    name: z.string(),
    /** Product or reference number for the catalog item */
    productNumber: z.string(),
    /** Manufacturer name associated with the catalog item */
    manufacturer: z.string(),
    /** Catalog item description if available */
    description: z.string().optional(),
    /** Cost of the item if provided */
    cost: z.number().optional(),
    /** Additional metadata for the catalog item */
    metadata: z.record(z.unknown()).nullish(),
    /** Note from preference card for this catalog item */
    note: z.string().optional(),
    /** Checklist items for this catalog item */
    items: z.array(ChecklistUntrackedItemDtoSchema),
  });
export type UntrackedCatalogItemGroupDto = z.infer<
  typeof UntrackedCatalogItemGroupDtoSchema
>;

/**
 * Case build checklist tracked items DTO
 */
export const CaseBuildChecklistTrackedItemsDtoSchema = z
  .object({
    /** Tracked trays required for the checklist, grouped by catalog item */
    trays: z.array(CatalogItemGroupDtoSchema),
    /** Tracked equipment required for the checklist, grouped by catalog item */
    equipment: z.array(CatalogItemGroupDtoSchema),
    /** Tracked implants required for the checklist, grouped by catalog item */
    implants: z.array(CatalogItemGroupDtoSchema),
    /** Tracked stretchers required for the checklist, grouped by catalog item */
    stretchers: z.array(CatalogItemGroupDtoSchema),
    /** Tracked case carts required for the checklist, grouped by catalog item */
    caseCarts: z.array(CatalogItemGroupDtoSchema),
    /** Unexpected tracked items (added beyond preference card), grouped by catalog item */
    unexpectedItems: z.array(CatalogItemGroupDtoSchema),
  });
export type CaseBuildChecklistTrackedItemsDto = z.infer<
  typeof CaseBuildChecklistTrackedItemsDtoSchema
>;

/**
 * Case build checklist untracked items DTO
 */
export const CaseBuildChecklistUntrackedItemsDtoSchema = z
  .object({
    /** Single packed instruments (untracked) required for the checklist, grouped by catalog item */
    singlePackedInstruments: z.array(UntrackedCatalogItemGroupDtoSchema),
    /** Untracked instruments required for the checklist, grouped by catalog item */
    instruments: z.array(UntrackedCatalogItemGroupDtoSchema),
    /** Untracked supplies required for the checklist, grouped by catalog item */
    supplies: z.array(UntrackedCatalogItemGroupDtoSchema),
  });
export type CaseBuildChecklistUntrackedItemsDto = z.infer<
  typeof CaseBuildChecklistUntrackedItemsDtoSchema
>;

/**
 * Case build checklist summary DTO
 */
export const CaseBuildChecklistSummaryDtoSchema = z
  .object({
    /** Checklist identifier */
    id: z.string(),
    /** Build status */
    buildStatus: CaseBuildStatusSchema,
    /** Total count of trays required (sum of quantities from preference card tracked items where type is TRAY) */
    traysTotal: z.number(),
    /** Count of allocated trays (checklist items where type is TRAY and allocated = true) */
    traysAllocated: z.number(),
    /** Count of reserved trays (checklist items where type is TRAY, assetId is set and allocated = false) */
    traysReserved: z.number().optional(),
    /** Count of trays that are missing/unavailable (checklist items where type is TRAY and required but not allocated/reserved) */
    traysMissing: z.number().optional(),
    /** Preference card ID used to create this checklist */
    preferenceCardId: z.string(),
    /** Preference card version used to create this checklist */
    preferenceCardVersion: z.number(),
    /** Optional note for the checklist (max 300 characters) */
    note: z.string().nullish(),
    /** How the case was closed (set when a case is marked FINISHED). Not overwritten if already set. */
    closingMethod: ClosingMethodSchema.nullish(),
    /** Tracked items grouped by asset type - only included when includeChecklistItems=true or in detail endpoints */
    trackedItems: CaseBuildChecklistTrackedItemsDtoSchema.optional(),
    /** Untracked items grouped by type - only included when includeChecklistItems=true or in detail endpoints */
    untrackedItems: CaseBuildChecklistUntrackedItemsDtoSchema.optional(),
  });
export type CaseBuildChecklistSummaryDto = z.infer<
  typeof CaseBuildChecklistSummaryDtoSchema
>;

/**
 * Untracked asset type enumeration for preference cards
 */
export enum UntrackedAssetType {
  SUPPLY = 'SUPPLY',
  SINGLE_PACKED_INSTRUMENT = 'SINGLE_PACKED_INSTRUMENT',
  INSTRUMENT = 'INSTRUMENT',
}

export const UntrackedAssetTypeSchema = z.nativeEnum(UntrackedAssetType);

/**
 * Available asset location information for tracked catalog items
 */
export const AvailableAssetLocationDtoSchema = z
  .object({
    /** Location identifier (null for assets without a location) */
    locationId: z.string().nullable(),
    /** Display name of the location */
    locationName: z.string(),
    /** Number of available assets at this location */
    count: z.number(),
  });
export type AvailableAssetLocationDto = z.infer<
  typeof AvailableAssetLocationDtoSchema
>;

/**
 * Catalog item row within a tracked preference card asset group
 */
export const TrackedAssetCatalogItemDtoSchema = z
  .object({
    /** Tracked catalog item identifier */
    catalogItemId: z.string(),
    /** Catalog item type */
    type: AssetTypeSchema,
    /** Display name for the catalog item */
    name: z.string(),
    /** Manufacturer name associated with the catalog item */
    manufacturer: z.string(),
    /** Product or reference number for the catalog item */
    productNumber: z.string(),
    /** Catalog item description if available */
    description: z.string().optional(),
    /** Quantity required for this preference card row */
    quantity: z.number(),
    /** Optional note specific to this preference card row */
    note: z.string().optional(),
    /** Additional metadata for the catalog item */
    metadata: z.record(z.unknown()).nullish(),
    /** Total number of available assets for this catalog item */
    availableCount: z.number(),
    /** Available assets grouped by their storage location */
    availableAssetLocations: z.array(AvailableAssetLocationDtoSchema),
  });
export type TrackedAssetCatalogItemDto = z.infer<
  typeof TrackedAssetCatalogItemDtoSchema
>;

/**
 * Tracked assets grouped by asset type
 */
export const PreferenceCardTrackedAssetsDtoSchema = z
  .object({
    /** Tracked case carts required for the preference card */
    caseCarts: z.array(TrackedAssetCatalogItemDtoSchema),
    /** Tracked trays required for the preference card */
    trays: z.array(TrackedAssetCatalogItemDtoSchema),
    /** Tracked equipment required for the preference card */
    equipment: z.array(TrackedAssetCatalogItemDtoSchema),
    /** Tracked implants required for the preference card */
    implants: z.array(TrackedAssetCatalogItemDtoSchema),
    /** Tracked stretchers required for the preference card */
    stretchers: z.array(TrackedAssetCatalogItemDtoSchema),
  });
export type PreferenceCardTrackedAssetsDto = z.infer<
  typeof PreferenceCardTrackedAssetsDtoSchema
>;

/**
 * Untracked asset row for preference cards
 */
export const PreferenceCardUntrackedAssetDtoSchema = z
  .object({
    /** Identifier for the preference card untracked asset row */
    id: z.string(),
    /** Identifier of the catalog item used by this row */
    untrackedAssetCatalogId: z.string(),
    /** Type of the untracked asset */
    type: UntrackedAssetTypeSchema,
    /** Display name for the untracked asset */
    name: z.string(),
    /** Hospital product number */
    productNumber: z.string(),
    /** Manufacturer name */
    manufacturer: z.string(),
    /** Description of the untracked asset */
    description: z.string().optional(),
    /** Cost of the item if provided */
    cost: z.number().optional(),
    /** Quantity required for this preference card */
    quantity: z.number(),
    /** Note specific to this preference card */
    note: z.string().optional(),
    /** Additional metadata associated with the untracked asset */
    metadata: z.record(z.unknown()).nullish(),
  });
export type PreferenceCardUntrackedAssetDto = z.infer<
  typeof PreferenceCardUntrackedAssetDtoSchema
>;

/**
 * Untracked assets grouped by asset grouping
 */
export const PreferenceCardUntrackedAssetsDtoSchema = z
  .object({
    /** Untracked supplies required for the preference card */
    supplies: z.array(PreferenceCardUntrackedAssetDtoSchema),
    /** Untracked single packed instruments required for the preference card */
    singlePackedInstruments: z.array(PreferenceCardUntrackedAssetDtoSchema),
    /** Untracked instruments required for the preference card */
    instruments: z.array(PreferenceCardUntrackedAssetDtoSchema),
  });
export type PreferenceCardUntrackedAssetsDto = z.infer<
  typeof PreferenceCardUntrackedAssetsDtoSchema
>;

/**
 * Case type details associated with a preference card
 */
export const PreferenceCardCaseTypeDtoSchema = z
  .object({
    /** Case type identifier */
    id: z.string(),
    /** Hospital case type identifier */
    hospitalCaseTypeId: z.string(),
    /** Case type display name */
    displayName: z.string(),
    /** Procedure code */
    procedureCode: z.string(),
    /** Specialty associated with the case type */
    specialty: z.string().nullable(),
  });
export type PreferenceCardCaseTypeDto = z.infer<
  typeof PreferenceCardCaseTypeDtoSchema
>;

/**
 * Preference card response DTO
 */
export const PreferenceCardResponseDtoSchema = z
  .object({
    /** Preference card ID */
    preferenceCardId: z.string(),
    /** Hospital preference card ID */
    hospitalPreferenceCardId: z.string(),
    /** Surgeon details */
    surgeon: z.object({
      id: z.string(),
      npiNumber: z.string().optional(),
      fullName: z.string(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
    }),
    /** Case type details */
    caseType: PreferenceCardCaseTypeDtoSchema,
    /** Version number */
    version: z.number(),
    /** Whether this version is active */
    isActive: z.boolean(),
    /** Tracked catalog items grouped by asset type */
    trackedAssets: PreferenceCardTrackedAssetsDtoSchema,
    /** Grouped untracked assets required for the preference card */
    untrackedAssets: PreferenceCardUntrackedAssetsDtoSchema,
  });
export type PreferenceCardResponseDto = z.infer<
  typeof PreferenceCardResponseDtoSchema
>;

/**
 * Cart location details (from selectedCart)
 */
export const SelectedCartLocationDtoSchema = z
  .object({
    departmentOrUnit: z.string().nullish(),
    name: z.string().nullish(),
    floor: z.string().nullish(),
    locationDisplayName: z.string().nullish(),
  });
export type SelectedCartLocationDto = z.infer<
  typeof SelectedCartLocationDtoSchema
>;

/**
 * Selected cart assignment details
 */
export const SelectedCartDtoSchema = z
  .object({
    assetId: z.string(),
    name: z.string().optional(),
    referenceNumber: z.string(),
    status: AssetStatusSchema.nullish(),
    location: SelectedCartLocationDtoSchema.nullish(),
  });
export type SelectedCartDto = z.infer<typeof SelectedCartDtoSchema>;

/**
 * Facility metadata for a linked case location
 */
export const CaseLocationFacilityDtoSchema = z.object({
  /** Facility identifier */
  id: z.string(),
  /** Facility name */
  name: z.string(),
});
export type CaseLocationFacilityDto = z.infer<
  typeof CaseLocationFacilityDtoSchema
>;

/**
 * Location type metadata for a linked case location
 */
export const CaseLocationTypeDtoSchema = z.object({
  /** Location type identifier */
  id: z.string(),
  /** Location type name */
  name: z.string(),
});
export type CaseLocationTypeDto = z.infer<typeof CaseLocationTypeDtoSchema>;

/**
 * Linked system location details for a case
 */
export const CaseLocationDtoSchema = z.object({
  /** Location identifier */
  id: z.string(),
  /** Location name */
  name: z.string(),
  /** Department or unit */
  departmentOrUnit: z.string(),
  /** Floor identifier */
  floor: z.string(),
  /** Location type */
  locationType: CaseLocationTypeDtoSchema.nullish(),
  /** Facility details */
  facility: CaseLocationFacilityDtoSchema,
});
export type CaseLocationDto = z.infer<typeof CaseLocationDtoSchema>;

/**
 * Case response DTO matching backend API structure
 * This is the main DTO returned by the case detail and list endpoints
 */
export const CaseResponseDtoSchema = z
  .object({
    /** Internal case identifier (UUID) */
    id: z.string(),
    /** External case identifier */
    caseId: z.string(),
    /** Surgeon id */
    surgeonId: z.string(),
    /** Surgeon full name */
    surgeonName: z.string().optional(),
    /** Case type id */
    caseTypeId: z.string(),
    /** Case type procedure code */
    caseTypeProcedureCode: z.string().optional(),
    /** Case type display name */
    caseTypeDisplayName: z.string().optional(),
    /** OR Room */
    room: z.string().nullish(),
    /** Linked system location details */
    location: CaseLocationDtoSchema.nullish(),
    /** Scheduled start time */
    scheduledDateTime: z.union([z.string(), z.date()]),
    /** Duration in minutes */
    duration: z.number().nullish(),
    /** Case status */
    status: CaseStatusSchema,
    /** Dispatch status */
    dispatchStatus: z.boolean(),
    /** When the OR confirmed all required assets are present in the room */
    verifiedAt: z.union([z.string(), z.date()]).nullish(),
    /** Display name of the user who confirmed the assets */
    verifiedByName: z.string().nullish(),
    /** Build checklist summary */
    buildChecklist: CaseBuildChecklistSummaryDtoSchema,
    /** Computed cart completeness status for API consumers */
    cartCompletenessStatus: z
      .union([CartCompletenessStatusSchema, z.string()])
      .nullish(),
    /** Selected cart assignment details (assetId, referenceNumber, status, location) */
    selectedCart: SelectedCartDtoSchema.nullish(),
  });
export type CaseResponseDto = z.infer<typeof CaseResponseDtoSchema>;

/**
 * Response from the cases API
 */
export const CasesResponseDTOSchema = z
  .object({
    /** Array of surgical cases */
    cases: z.array(CaseResponseDtoSchema),
    /** Total number of cases available */
    total: z.number(),
    /** Whether there are more pages available */
    hasMore: z.boolean(),
    /** Current page number */
    page: z.number(),
    /** Number of items per page */
    pageSize: z.number(),
    /** Statistics for all cases (not just loaded ones) */
    statistics: z
      .object({
        totalCases: z.number(),
        activeCases: z.number(),
        completedCases: z.number(),
        notStartedCases: z.number(),
      })
      .optional(),
  });
export type CasesResponseDTO = z.infer<typeof CasesResponseDTOSchema>;

/**
 * @deprecated Use CaseResponseDto instead. This DTO is kept for backward compatibility.
 * Surgical case DTO (Data Transfer Object) for API communication
 * This represents the raw data structure from the API
 */
export interface SurgicalCaseDTO {
  /** Unique identifier for the case (e.g., "CS-1029991") */
  id: string;
  /** Name of the surgeon (e.g., "Dr Smith") */
  surgeon: string;
  /** Type of surgical procedure (e.g., "Total Hip Arthroplasty") */
  caseType: string;
  /** Operating room location (e.g., "OR Suite A") */
  room: string;
  /** Date and time of the surgery */
  dateTime: Date;
  /** Duration of the surgery in minutes */
  duration: number;
  /** Whether the case is currently active */
  caseActiveStatus: boolean;
  /** Cart ID (e.g., "0002") */
  cartID: string;
  /** Cart location (e.g., "OR Suite A") */
  cartLocation: string;
  /** Number of trays required for the surgery */
  traysRequired: number;
  /** Number of trays currently loaded */
  traysLoaded: number;
  /** Current status of cart preparation */
  cartCompletenessStatus: CartCompletenessStatus;
  /** Whether the cart has been checked out */
  checkedOutStatus: boolean;
}
